import { inject, injectable } from 'inversify';
import BaseRepository from '@/creditflowresearch/infrastructure/repository/BaseRepository';
import { type IDispatcher } from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr';
import DITypes from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr/DITypes';
import Request from '@framework/api/Rpc/Request';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import {
  ArchiveByOptions,
  ITraceFilterByHashResponse,
  ITraceFilterResponse,
  ITraceIssuersResponse,
  ITracePayload,
  ITraceResponse,
} from '@/condor/domain/vo/types';
import { IMarketTradesPayload, TTraceFiltersPayload } from '@/creditflowresearch/domain/vo/Trace/IMarketTradesPayload';
import { ITraceChartItem, ITraceChartPayload } from '@/creditflowresearch/domain/vo/Trace/ITraceChartData';

@injectable()
class TraceRepository extends BaseRepository {
  constructor(
    @inject(DITypes.JsonRpcDispatcher) protected readonly rpc: IDispatcher,
  ) {
    super(rpc);
  }

  getTradesRequest = async (payload: ITracePayload): Promise<ITraceResponse> => this.abortableCall(new Request('finsight_web.bond.trace.get_trades', payload));

  getDealsRequest = async (payload) => this.abortableCall(new Request('finsight_web.deal_transaction.deal.get_deals', payload));

  mapTradeRequest = async (payload) => this.abortableCall(new Request('finsight_web.bond.trace.map_trade', payload));

  archiveTrade = async (payload: { cusip: string | null, archiveBy: ArchiveByOptions, issuerName: string | null }) => {
    let response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.bond.trace.archive_trace', payload));
    return response.getResult().payload;
  };

  restoreTrade = async (payload: { cusip: string }) => this.abortableCall(new Request('finsight_web.bond.trace.restore_trace', payload));

  getMarketTrades = async (payload: IMarketTradesPayload) => this.abortableCall(
      new Request('finsight_web.market.bond.trace.get_trades_tile', payload),
  );

  getTraceIssuersFilter = async (filter: TTraceFiltersPayload, search: string): Promise<ITraceIssuersResponse> => {
    const payload = { filter, search };
    return this.abortableCall(
        new Request('finsight_web.market.bond.trace.filter.get_issuers', payload),
    );
  };

  getTraceFilter = async (filter: TTraceFiltersPayload): Promise<ITraceFilterResponse> => {
    const payload = { filter };
    return this.abortableCall(
        new Request('finsight_web.market.bond.trace.filter.get_boundary_filter', payload),
    );
  };

  getTraceFilterByHash = async (hash: string): Promise<ITraceFilterByHashResponse> => {
    const payload = { hash };
    return this.abortableCall(
        new Request('finsight_web.bond.trace.filter.get_by_hash', payload),
    );
  };

  getTraceChartData = async (payload: ITraceChartPayload): Promise<ITraceChartItem[]> => {
    return this.abortableCall(
        new Request('finsight_web.market.bond.trace.get_chart_data', payload),
    );
  };
}

export default TraceRepository;
